<template>
  <div class="language-picker">
    <Dropdown
      class="language-picker__dropdown"
      v-model="currentLanguage"
      aria-label="Language"
      :options="availableLanguages"
      @onSelect="goToLanguage">
      <span
        class="icon icon--globe language-picker__icon"
        aria-hidden="true" />
      <span class="language-picker__value">{{ currentLanguage }}</span>
    </Dropdown>
  </div>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, ref } from "vue";
import { useAppStore } from "@app/Store/app-store";
import { useApi } from "@shared/Services/axios-service";
import Dropdown from "@shared/Components/Dropdown/Dropdown.vue";

type LanguageItem = {
  code: string;
  name: string;
  url: string;
};

type LanguagesResponse = {
  currentLanguage: string;
  languageItems: LanguageItem[];
};

const LANGUAGE_CODES = {
  English: "EN",
  German: "DE",
  Swedish: "SV",
} as const;

const LANGUAGE_LOCALE_BY_CODE: Record<string, string> = {
  [LANGUAGE_CODES.English]: "en-US",
  [LANGUAGE_CODES.German]: "de-DE",
  [LANGUAGE_CODES.Swedish]: "sv-SE",
};
const DEFAULT_LANGUAGE_LOCALE = LANGUAGE_LOCALE_BY_CODE[LANGUAGE_CODES.Swedish];

const appStore = useAppStore();
const { data: languagesData, get: getLanguages } = useApi<LanguagesResponse>("/layout/languages");
const currentLanguage = ref("");

const availableLanguages = computed(() =>
  languagesData.value?.languageItems.map((item) => item.code) ?? []
);

onBeforeMount(async () => {
  const response = await getLanguages().catch(() => undefined);
  if (!response?.currentLanguage) {
    return;
  }

  currentLanguage.value = response.currentLanguage;
  saveCurrentLanguage(currentLanguage.value);
});

function goToLanguage(): void {
  const selectedLanguage = languagesData.value?.languageItems.find((item) => item.code === currentLanguage.value);
  if (!selectedLanguage?.url) {
    return;
  }

  saveCurrentLanguage(currentLanguage.value);
  window.location.replace(new URL(selectedLanguage.url, window.location.origin).toString());
}

function saveCurrentLanguage(lang: string): void {
  appStore.setCurrentLanguage(getLanguageString(lang));
}

function getLanguageString(lang: string): string {
  return LANGUAGE_LOCALE_BY_CODE[lang] ?? DEFAULT_LANGUAGE_LOCALE;
}
</script>
